import { useQuery } from '@tanstack/react-query';
import {
  stockLookupService,
  type StockLookupComboRow,
  type StockLookupLotRow,
  type StockLookupListResponse,
  type StockLookupMode,
  type StockLookupProductRow,
  type StockLookupQuery,
  type StockLookupSerialRow,
} from '@/services/stockLookup.service';

const KEY_ALL = ['stock-lookup'] as const;

const FETCHERS = {
  products: stockLookupService.products,
  lots: stockLookupService.lots,
  serials: stockLookupService.serials,
  combos: stockLookupService.combos,
} as const;

/** Ánh xạ mode → row type để `useStockLookupList` trả đúng kiểu cho từng chế độ. */
interface StockLookupRowByMode {
  products: StockLookupProductRow;
  lots: StockLookupLotRow;
  serials: StockLookupSerialRow;
  combos: StockLookupComboRow;
}

/**
 * TCR-1954 — dữ liệu cho bảng chính của popup tra cứu.
 *
 * `enabled` theo trạng thái mở của modal: popup nằm trong Header nên nó mount ở MỌI trang;
 * không gate thì mỗi lần app load là bắn 1 request tra cứu tồn kho vô ích.
 */
export function useStockLookupList<M extends StockLookupMode>(
  mode: M,
  query: StockLookupQuery,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [...KEY_ALL, mode, query] as const,
    queryFn: () =>
      FETCHERS[mode](query) as Promise<
        StockLookupListResponse<StockLookupRowByMode[M]>
      >,
    placeholderData: (prev: StockLookupListResponse<StockLookupRowByMode[M]> | undefined) => prev,
    enabled: options.enabled ?? true,
  });
}

/**
 * `warehouseId` nằm TRONG `queryKey`: hai lần mở popup cho cùng một mặt hàng nhưng khác
 * bộ lọc kho là hai kết quả khác nhau, dùng chung key sẽ trả cache của lần trước.
 */
export function useWarehouseBreakdown(
  variantId: string | null,
  warehouseId?: string,
  storeId?: string,
) {
  return useQuery({
    queryKey: [...KEY_ALL, 'warehouse-breakdown', variantId, warehouseId, storeId] as const,
    queryFn: () =>
      stockLookupService.warehouseBreakdown({
        variant_id: variantId as string,
        warehouse_id: warehouseId,
        store_id: storeId,
      }),
    enabled: Boolean(variantId),
  });
}

export function useReservedOrders(
  variantId: string | null,
  warehouseId?: string,
  storeId?: string,
) {
  return useQuery({
    queryKey: [...KEY_ALL, 'reserved-orders', variantId, warehouseId, storeId] as const,
    queryFn: () =>
      stockLookupService.reservedOrders({
        variant_id: variantId as string,
        warehouse_id: warehouseId,
        store_id: storeId,
      }),
    enabled: Boolean(variantId),
  });
}
